import React from 'react';
import { User as UserIcon, Mail, Phone, Building2, CheckCircle2, Clock, ChevronRightCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../../lib/cn';
import {
    Lead, LeadColor, LEAD_SOURCE_LABELS, LEAD_SOURCE_ICONS, LEAD_SOURCE_COLORS,
    leadStaleness, leadNextActionDue, LEAD_LOST_REASON_LABELS, LEAD_COLOR_META,
} from '../../types/leads.types';
import { LeadQuickActions } from './LeadQuickActions';
import { LeadColorPicker } from './LeadColorPicker';

// classi del chip "giorni fermo" per livello di staleness
const STALE_CHIP_CLASS = {
    fresh: 'bg-gray-100 text-gray-500',
    warn: 'bg-amber-100 text-amber-700 border border-amber-200',
    stale: 'bg-red-100 text-red-700 border border-red-200',
} as const;

interface LeadCardProps {
    lead: Lead;
    onClick?: () => void;
    className?: string;
    compact?: boolean;
    hasDueReminder?: boolean;
    // se passato, mostra le azioni rapide su hover e ricarica dopo il log
    onQuickLogged?: () => void;
    // se passato, mostra il pallino colore e notifica la scelta (null = nessuno)
    onColorChange?: (color: LeadColor | null) => void;
}

function initials(lead: Lead): string {
    const base = lead.full_name || lead.company_name || '?';
    return base
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('');
}

export function LeadCard({ lead, onClick, className, compact, hasDueReminder, onQuickLogged, onColorChange }: LeadCardProps) {
    const Icon = LEAD_SOURCE_ICONS[lead.source] || UserIcon;
    const title = lead.full_name || lead.company_name || 'Lead senza nome';
    const subtitle = lead.full_name && lead.company_name ? lead.company_name : null;
    // contatore "giorni fermo": solo stadi attivi e almeno 1 giorno dall'ultimo contatto
    const stale = leadStaleness(lead);
    const showStale = stale.applicable && stale.days >= 1;
    const nextDue = leadNextActionDue(lead);
    const lostLabel = lead.stage === 'perso' && lead.lost_reason
        ? (LEAD_LOST_REASON_LABELS[lead.lost_reason.split(':')[0]] || lead.lost_reason)
        : null;
    const colorMeta = lead.color ? LEAD_COLOR_META[lead.color] : null;

    return (
        <div
            onClick={onClick}
            className={cn(
                'group bg-white rounded-lg border-2 p-3 shadow-sm hover:shadow-md transition-shadow',
                colorMeta ? colorMeta.borderClass : 'border-gray-200',
                onClick && 'cursor-pointer',
                className
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', LEAD_SOURCE_COLORS[lead.source])} />
                        <h4 className="text-sm font-semibold text-gray-900 truncate" title={title}>
                            {title}
                        </h4>
                        {hasDueReminder && (
                            <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" title="Reminder scaduto" />
                        )}
                        {showStale && (
                            <span
                                className={cn(
                                    'ml-auto flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0',
                                    STALE_CHIP_CLASS[stale.level]
                                )}
                                title={`Ultimo contatto ${stale.days} giorni fa`}
                            >
                                <Clock className="w-2.5 h-2.5" /> {stale.days}g
                            </span>
                        )}
                    </div>
                    {subtitle && (
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                            <Building2 className="w-3 h-3" /> {subtitle}
                        </p>
                    )}
                </div>
                {lead.converted_customer_id && (
                    lead.stage === 'vinto' ? (
                        <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                            <CheckCircle2 className="w-3 h-3" /> Cliente
                        </span>
                    ) : (
                        <span className="flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                            <UserIcon className="w-3 h-3" /> Prospect
                        </span>
                    )
                )}
            </div>

            {!compact && (
                <div className="mt-2 space-y-1 text-xs text-gray-600">
                    {lead.email && (
                        <p className="flex items-center gap-1.5 truncate">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{lead.email}</span>
                        </p>
                    )}
                    {lead.phone && (
                        <p className="flex items-center gap-1.5 truncate">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{lead.phone}</span>
                        </p>
                    )}
                </div>
            )}

            {lead.next_action && (
                <div
                    className={cn(
                        'mt-2 flex items-start gap-1 rounded-md px-1.5 py-1 text-[11px]',
                        nextDue ? 'bg-red-50 text-red-700' : 'bg-indigo-50 text-indigo-700'
                    )}
                    title="Prossima azione"
                >
                    <ChevronRightCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="min-w-0">
                        <span className="font-medium truncate block">{lead.next_action}</span>
                        {lead.next_action_at && (
                            <span className="opacity-80">
                                {format(new Date(lead.next_action_at), 'd MMM, HH:mm', { locale: it })}
                            </span>
                        )}
                    </span>
                </div>
            )}

            {lostLabel && (
                <div className="mt-2 flex items-center gap-1 text-[11px] text-red-600">
                    <XCircle className="w-3 h-3 flex-shrink-0" /> {lostLabel}
                </div>
            )}

            {onQuickLogged && (
                <div className="mt-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <LeadQuickActions lead={lead} onLogged={onQuickLogged} compact />
                </div>
            )}

            <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] text-gray-400">
                    {LEAD_SOURCE_LABELS[lead.source]} ·{' '}
                    {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true, locale: it })}
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {lead.assignee && (
                        <span
                            className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0"
                            title={lead.assignee.full_name || lead.assignee.email}
                        >
                            {initials(lead)}
                        </span>
                    )}
                    {onColorChange && (
                        <LeadColorPicker value={lead.color ?? null} onChange={onColorChange} />
                    )}
                </div>
            </div>
        </div>
    );
}

export default LeadCard;
